import {Component, EventEmitter, Inject, OnInit, Output} from '@angular/core';
import {DatePipe} from '@angular/common';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {Observable, of} from 'rxjs';
import {catchError, switchMap} from 'rxjs/operators';
import {InsuredPersonDTO} from '../../../../ent/models/InsuredPersonDTO';
import {BasicPersonDetailDTO} from '../../../../ent/models/person/BasicPersonDetailDTO';
import {InsuredPersonService} from '../../../../ent/services/insured-person.service';
import {DateUtil} from '../../../util/DateUtil';
import {SharedService} from '../../../services/shared-service';
import {SharedClmService} from '../../../../clm/services/shared-clm.service';
import { CoverStatusLinkDTO } from 'src/app/modules/ent/models/CoverStatusLinkDTO';

@Component({
  selector: 'app-member-overview',
  templateUrl: './member-overview.component.html',
  styleUrls: ['../../../../../../styles/global/_dialog-styles.scss', './member-overview.component.scss']
})
export class MemberOverviewComponent implements OnInit {

  readonly dateFormat = DateUtil.dateFormat;
  insuredPersonDTO: InsuredPersonDTO;
  noOfConsultationVisits: number;
  
  constructor(
    public datePipe: DatePipe,
    public dialogRef: MatDialogRef<MemberOverviewComponent>,
    private insuredPersonService: InsuredPersonService,
    private sharedService: SharedService,
    private sharedClmService: SharedClmService,
    @Inject(MAT_DIALOG_DATA) public basicPersonDetails: BasicPersonDetailDTO
  ) {
  }

  ngOnInit(): void {
    if (this.basicPersonDetails.personId) {
      this.getInsuredPersonById(this.basicPersonDetails.personId)
        .pipe(
          switchMap((response: InsuredPersonDTO) => {
            this.insuredPersonDTO = response;
            const insuredPersonId: number = this.insuredPersonDTO.insuredPersonId;
            return this.sharedClmService.getConsultationVisitsBy(insuredPersonId)
              .pipe(
                catchError((errorResponse) => {
                  return of(errorResponse);
                })
              );
          }),
        )
        .subscribe({
          next: ({visits}: { visits: number }) => {
            this.noOfConsultationVisits = visits;
          },
          error: ({error}) => {
            console.error('Error:', error);
            this.sharedService.openErrorDialog(error);
          }
        });
    }
  }

  getInsuredPersonById(insuredPersonId: number): Observable<InsuredPersonDTO> {
    return this.insuredPersonService.getInsuredPerson(insuredPersonId);
  }

  getStartAndEndDate(startDate, endDate): string {
    return `${this.datePipe.transform(startDate, this.dateFormat)} - ${endDate != null ? this.datePipe.transform(endDate, this.dateFormat) : 'current'} `;
  }

  getBirthDateString(birthDate: Date): string {
    return `${this.datePipe.transform(birthDate, this.dateFormat)} ${this.getAgeInYearsAndDays(birthDate)}`;
  }

  getLatestCoverStatusStartAndEndDate(coverStatuses: CoverStatusLinkDTO[], status: string): string {
    if (status === 'Activated' && this.insuredPersonDTO?.startDate) {
      return this.getStartAndEndDate(this.insuredPersonDTO.startDate, this.insuredPersonDTO.endDate);
    }

    if (!coverStatuses?.length) {
      return '';
    }

    const filtered = coverStatuses
                      .filter(cs => cs.coverStatus?.name === status)
                      .sort((a, b) =>
                        new Date(b.startDate).getTime() - new Date(a.startDate).getTime()
                      );

    if (!filtered?.length) {
      return 'Detailed information unavailable';
    }

    return this.getStartAndEndDate(filtered[0].startDate, filtered[0].endDate);

  }

  private getAgeInYearsAndDays(birthdate: Date): string {
    birthdate = new Date(birthdate);
    const today = new Date();
    let years = today.getFullYear() - birthdate.getFullYear();
    let days = 0;

    // Adjust years if the birthdate hasn't occurred yet this year
    if (today.getMonth() < birthdate.getMonth() ||
      (today.getMonth() === birthdate.getMonth() && today.getDate() < birthdate.getDate())) {
      years--;
    }

    // Calculate days
    const lastBirthday = new Date(today.getFullYear(), birthdate.getMonth(), birthdate.getDate());
    if (today < lastBirthday) {
      lastBirthday.setFullYear(today.getFullYear() - 1);
    }
    days = Math.floor((today.getTime() - lastBirthday.getTime()) / (1000 * 60 * 60 * 24));

    return `(${years} years, ${days} days)`;
  }

}
